/**
 * Pure helpers behind `/tools/encode/base64`. Tests live in
 * `tests/web/base64Tool.test.ts`.
 *
 * Why we don't use `btoa` / `atob` directly:
 *   - They throw on characters outside Latin-1, so '中文' fails;
 *   - They don't accept Uint8Array, so binary needs a manual loop;
 *   - They don't tolerate the missing-padding form ('YWJj' vs 'YWJj==').
 *
 * The implementation here delegates encoding to `TextEncoder` so the
 * UTF-8 round-trip is correct, and decoding to a small base64
 * alphabet decoder so we can support both standard and URL-safe
 * variants in one pass.
 */

export type Base64Variant = 'standard' | 'urlsafe';

export interface Base64EncodeOptions {
  variant?: Base64Variant;
  /** Drop trailing '=' padding when true (default: keep). */
  noPadding?: boolean;
}

const STD_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function encodeBase64Text(
  input: string,
  opts: Base64EncodeOptions = {},
): string {
  const bytes = new TextEncoder().encode(input);
  return encodeBase64Bytes(bytes, opts);
}

export function encodeBase64Bytes(
  bytes: Uint8Array,
  opts: Base64EncodeOptions = {},
): string {
  const alphabet = opts.variant === 'urlsafe' ? URL_ALPHABET : STD_ALPHABET;
  let out = '';
  let i = 0;
  // Walk the byte stream three at a time — each triple maps to four
  // base64 chars; trailing 1- or 2-byte groups produce '==' / '='
  // padding that we may strip below.
  for (; i + 2 < bytes.length; i += 3) {
    const a = bytes[i];
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += alphabet[a >> 2];
    out += alphabet[((a & 0x03) << 4) | (b >> 4)];
    out += alphabet[((b & 0x0f) << 2) | (c >> 6)];
    out += alphabet[c & 0x3f];
  }
  const rem = bytes.length - i;
  if (rem === 1) {
    const a = bytes[i];
    out += alphabet[a >> 2];
    out += alphabet[(a & 0x03) << 4];
    out += '==';
  } else if (rem === 2) {
    const a = bytes[i];
    const b = bytes[i + 1];
    out += alphabet[a >> 2];
    out += alphabet[((a & 0x03) << 4) | (b >> 4)];
    out += alphabet[(b & 0x0f) << 2];
    out += '=';
  }
  if (opts.noPadding) return out.replace(/=+$/, '');
  return out;
}

export type Base64Decoded =
  | { ok: true; text: string; bytes: Uint8Array }
  | { ok: false; error: string };

/**
 * Auto-detects standard vs URL-safe variants and tolerates missing
 * padding. Whitespace (spaces, tabs, newlines) is stripped because
 * it's almost always a paste artefact.
 */
export function decodeBase64(input: string): Base64Decoded {
  const cleaned = input.replace(/\s+/g, '');
  if (cleaned.length === 0) return { ok: false, error: '输入为空' };

  // Reject non-alphabet characters early so we can give a precise
  // pointer instead of a generic "invalid base64".
  const bad = cleaned.match(/[^A-Za-z0-9+/=\-_]/);
  if (bad) {
    return { ok: false, error: `非法 Base64 字符: '${bad[0]}'` };
  }

  // Normalise URL-safe variant to the standard alphabet so we can
  // share the decode loop.
  const normalised = cleaned.replace(/-/g, '+').replace(/_/g, '/');

  // Re-pad if the user pasted a no-padding form. Length must be a
  // multiple of 4 once padding is in place.
  const padNeeded = (4 - (normalised.length % 4)) % 4;
  if (padNeeded === 3) {
    return { ok: false, error: 'Base64 长度无效（落在 4n+1）' };
  }
  const padded = normalised + '='.repeat(padNeeded);

  const lookup = new Uint8Array(128);
  for (let i = 0; i < STD_ALPHABET.length; i++) {
    lookup[STD_ALPHABET.charCodeAt(i)] = i;
  }
  // Mark '=' so we can tell padding apart from a '0' index byte.
  const PAD = 0xff;
  lookup['='.charCodeAt(0)] = PAD;

  const out = new Uint8Array((padded.length / 4) * 3);
  let outLen = 0;
  for (let i = 0; i < padded.length; i += 4) {
    const a = lookup[padded.charCodeAt(i)];
    const b = lookup[padded.charCodeAt(i + 1)];
    const c = lookup[padded.charCodeAt(i + 2)];
    const d = lookup[padded.charCodeAt(i + 3)];
    out[outLen++] = ((a & 0x3f) << 2) | ((b & 0x3f) >> 4);
    if (c !== PAD) out[outLen++] = ((b & 0x0f) << 4) | ((c & 0x3f) >> 2);
    if (d !== PAD) out[outLen++] = ((c & 0x03) << 6) | (d & 0x3f);
  }
  const bytes = out.slice(0, outLen);

  // We always try to decode as UTF-8. The fatal flag means non-UTF-8
  // input throws so we can flag binary data in the UI.
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    text = '<二进制数据，UTF-8 解码失败>';
  }
  return { ok: true, text, bytes };
}
